import { createReducer } from '@reduxjs/toolkit'

import { TExportTable } from 'types/GlobalExportTable'

import * as actions from '../../actions/ExportTables/actions'
import * as apiKeyActions from '../../actions/ApiKeys/actions'
import * as authenticationActions from '../../actions/Authentication/actions'

import { insertItems, upsertItem, removeItem } from 'redux/utils'

export interface IExportTableReducerType {
  data: { [key: string]: TExportTable }
  fetched: boolean
  assignedApiKeys: boolean
  isCreating: boolean
  isDeleting: boolean
  isUpdating: boolean
  isFetchingOne: string | null
}

// Initial state
const INITIAL_STATE: IExportTableReducerType = {
  data: {},
  fetched: false,
  assignedApiKeys: false,
  isCreating: false,
  isDeleting: false,
  isUpdating: false,
  isFetchingOne: null
}

// Reducer
const ExportTablesReducer = createReducer(INITIAL_STATE, (builder) => {
  builder
    // tryGetAllExportTables
    .addCase(actions.getAllSuccess, (state, action) => {
      state.fetched = true
      state.data = insertItems(action.payload)
    })

    // tryGetOneExportTable
    .addCase(actions.getOne, (state, action) => {
      state.isFetchingOne = action.payload
    })
    .addCase(actions.getOneSuccess, (state, action) => {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { widget, ...exportTable } = action.payload

      state.isFetchingOne = null
      state.data = upsertItem(state.data, exportTable as TExportTable)
    })
    .addCase(actions.getOneFailed, (state) => {
      state.isFetchingOne = null
    })

    // tryCreateExportTable
    .addCase(actions.create, (state) => {
      state.isCreating = true
    })
    .addCase(actions.createSuccess, (state, action) => {
      state.isCreating = false
      state.data = upsertItem(state.data, action.payload)
    })
    .addCase(actions.createFailed, (state) => {
      state.isCreating = false
    })

    // tryUpdateExportTable
    .addCase(actions.update, (state) => {
      state.isUpdating = true
    })
    .addCase(actions.updateSuccess, (state, action) => {
      state.isUpdating = false
      state.data = upsertItem(state.data, action.payload)
    })
    .addCase(actions.updateFailed, (state) => {
      state.isUpdating = false
    })

    // tryDeleteExportTable
    .addCase(actions.destroy, (state) => {
      state.isDeleting = true
    })
    .addCase(actions.destroySuccess, (state, action) => {
      state.isDeleting = false
      state.data = removeItem(state.data, action.payload)
    })
    .addCase(actions.destroyFailed, (state) => {
      state.isDeleting = false
    })

    // tryAssignApiKeysToExportTable
    .addCase(actions.assignApiKeys, (state) => {
      state.assignedApiKeys = true
    })
    .addCase(actions.assignApiKeysSuccess, (state, action) => {
      state.data[action.payload.id].api_keys = action.payload.apiKeys
      state.assignedApiKeys = false
    })
    .addCase(actions.assignApiKeysFailed, (state) => {
      state.assignedApiKeys = false
    })
    .addCase(
      apiKeyActions.assignExportTablesToApiKeySuccess,
      (state, action) => {
        const { id, exportTableIds, removedExportTableIds } = action.payload

        removedExportTableIds.forEach((exportTableId) => {
          state.data[exportTableId].api_keys = state.data[
            exportTableId
          ].api_keys.filter((apiKeyId) => apiKeyId !== id)
        })

        exportTableIds.forEach((exportTableId) => {
          if (state.data[exportTableId].api_keys.includes(id)) return
          state.data[exportTableId].api_keys.push(id)
        })
      }
    )
    .addCase(authenticationActions.signOut, () => INITIAL_STATE)
    .addDefaultCase((state) => state)
})

export default ExportTablesReducer
